<template>
  <div class="bg-white dark:bg-[#0c1427] p-5 rounded-md shadow-md flex flex-col min-h-[450px] custom-fields-panel">
    <h6 class="text-black dark:text-white font-semibold mb-6 text-center text-xl">{{ directT('settings.leadsSettings.customFields.management') }}
</h6>
    
    <div class="mb-5 flex-grow">
      <div v-if="loading" class="flex justify-center items-center h-40">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
      </div>
      
      <div v-else-if="customFields.length === 0" class="text-gray-500 italic">
        {{ directT('settings.leadsSettings.customFields.noFields') }}
      </div>
      
      <ul v-else class="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
        <li 
          v-for="(field, index) in customFields" 
          :key="index"
          class="flex items-center justify-between bg-white dark:bg-[#0c1427] p-3 rounded border border-gray-200 dark:border-[#1c2b50]"
        >
          <div class="flex flex-col">
            <span class="text-black dark:text-white font-medium">{{ field.label }}</span>
            <span class="text-xs text-gray-500">{{ getFieldTypeName(field.type) }}</span>
          </div>
          <div class="flex items-center gap-2">
            <button 
              @click="editField(index)" 
              class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <i class="material-symbols-outlined !text-md">edit</i>
            </button>
            <button 
              @click="deleteField(index)" 
              class="text-red-500 hover:text-red-700"
            >
              <i class="material-symbols-outlined !text-md">delete</i>
            </button>
          </div>
        </li>
      </ul>
    </div>
    
    <div class="flex justify-between flex-nowrap gap-2 w-full mt-auto">
      <button 
        @click="addNewField" 
        class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md flex items-center whitespace-nowrap"
      >
        <i class="material-symbols-outlined mr-1">add_circle</i>
        <span>{{ directT('settings.leadsSettings.customFields.addField') }}</span>
      </button>
      
      <button 
        @click="saveCustomFields" 
        class="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md whitespace-nowrap"
        :disabled="saving"
      >
        {{ saving ? directT('settings.leadsSettings.saving') : directT('settings.leadsSettings.saveChanges') }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, PropType } from "vue";
import { supabase } from "@/lib/supabaseClient";
import { createToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

// Translation files
const translationFiles = {
  en: enTranslations,
  he: heTranslations,
  ar: arTranslations
};

export interface CustomField {
  id?: string;
  label: string;
  key: string;
  type: 'text' | 'number' | 'date' | 'email' | 'phone' | 'select' | 'checkbox';
  options?: string[];
  required: boolean;
  organization_id?: string;
}

export default defineComponent({
  name: "CustomFieldsPanel",
  props: {
    organizationId: {
      type: String as PropType<string>,
      required: true
    }
  },
  emits: ["field-saved", "field-deleted"],
  
  setup(props, { emit }) {
    const customFields = ref<CustomField[]>([]);
    const loading = ref(true);
    const saving = ref(false);
    
    // i18n setup
    const { locale } = useI18n();
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string, params: Record<string, any> = {}): string => {
      try {
        // Split the key into parts (e.g., "settings.leadsSettings.customFields.title" -> ["settings.leadsSettings", "customFields", "title"])
        const parts = key.split('.');
        
        // Start with the current locale's translations
        let currentObj: any = translationFiles[locale.value as keyof typeof translationFiles];
        
        // Navigate through the nested objects
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          
          // If we can't go deeper, break
          if (!currentObj || typeof currentObj !== 'object' || !(part in currentObj)) {
            // Try English fallback
            currentObj = translationFiles.en;
            
            // Navigate through the English translations
            for (let j = 0; j <= i; j++) {
              if (!currentObj || typeof currentObj !== 'object' || !(parts[j] in currentObj)) {
                return key; // Return the key if not found in English either
              }
              currentObj = currentObj[parts[j] as keyof typeof currentObj];
            }
            
            // Continue with the remaining parts using English
            for (let j = i + 1; j < parts.length; j++) {
              if (!currentObj || typeof currentObj !== 'object' || !(parts[j] in currentObj)) {
                return key; // Return the key if not found
              }
              currentObj = currentObj[parts[j] as keyof typeof currentObj];
            }
            
            break;
          }
          
          // Move to the next level
          currentObj = currentObj[part as keyof typeof currentObj];
        }
        
        // At this point, currentObj should be the translation string
        let result = typeof currentObj === 'string' ? currentObj : key;
        
        // Replace parameters in the string if any
        if (params && Object.keys(params).length > 0) {
          Object.entries(params).forEach(([paramKey, paramValue]) => {
            result = result.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
          });
        }
        
        return result;
      } catch (error) {
        console.error(`Translation error for key: ${key}`, error);
        return key; // Return the key if any error occurs
      }
    };
    
    // Load custom fields
    const loadCustomFields = async () => {
      try {
        // Don't attempt to load if organizationId is not valid
        if (!props.organizationId) {
          console.warn('Cannot load custom fields: Missing organization ID');
          loading.value = false;
          return;
        }
        
        loading.value = true;
        const { data, error } = await supabase
          .from('customleadsfield')
          .select('*')
          .eq('organizationid', props.organizationId);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          // Take the first record for this organization
          // The content field contains the array of custom fields
          customFields.value = (data[0].content || []) as CustomField[];
        } else {
          customFields.value = [];
        }
      } catch (error) {
        console.error('Error loading custom fields:', error);
        createToast('Failed to load custom fields', 'error');
      } finally {
        loading.value = false;
      }
    };
    
    // Generate a valid key from a label
    const generateKey = (label: string): string => {
      return label
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');
    };
    
    // Add a new custom field
    const addNewField = () => {
      emit("field-saved", {
        action: 'add',
        field: {
          label: '',
          key: '',
          type: 'text',
          required: false
        } as CustomField
      });
    };
    
    // Edit an existing field
    const editField = (index: number) => {
      emit("field-saved", {
        action: 'edit',
        field: { ...customFields.value[index] },
        index
      });
    };
    
    // Delete a field
    const deleteField = (index: number) => {
      emit("field-deleted", {
        field: customFields.value[index],
        index
      });
    };
    
    // Save all custom fields
    const saveCustomFields = async () => {
      try {
        saving.value = true;
        
        // Check if we already have a record for this organization
        const { data, error } = await supabase
          .from('customleadsfield')
          .select('id')
          .eq('organizationid', props.organizationId);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          // Update existing record
          const { error: updateError } = await supabase
            .from('customleadsfield')
            .update({
              content: customFields.value,
              updated_at: new Date()
            })
            .eq('id', data[0].id);
          
          if (updateError) throw updateError;
        } else {
          // Create new record
          const { error: insertError } = await supabase
            .from('customleadsfield')
            .insert({
              organizationid: props.organizationId,
              content: customFields.value
            });
          
          if (insertError) throw insertError;
        }
        
        createToast('Custom fields saved successfully', 'success');
      } catch (error) {
        console.error('Error saving custom fields:', error);
        createToast('Failed to save custom fields', 'error');
      } finally {
        saving.value = false;
      }
    };
    
    // Helper to get field type name for display
    const getFieldTypeName = (type: string): string => {
      const typeMap: Record<string, string> = {
        'text': directT('settings.leadsSettings.customFields.typeText'),
        'number': directT('settings.leadsSettings.customFields.typeNumber'),
        'date': directT('settings.leadsSettings.customFields.typeDate'),
        'email': directT('settings.leadsSettings.customFields.typeEmail'),
        'phone': directT('settings.leadsSettings.customFields.typePhone'),
        'select': directT('settings.leadsSettings.customFields.typeSelect'),
        'checkbox': directT('settings.leadsSettings.customFields.typeCheckbox')
      };
      return typeMap[type] || type;
    };
    
    // Initialize component
    onMounted(() => {
      loadCustomFields();
    });
    
    return {
      customFields,
      loading,
      saving,
      addNewField,
      editField,
      deleteField,
      saveCustomFields,
      getFieldTypeName,
      directT
    };
  }
});
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(0,0,0,0.1);
  border-radius: 10px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0,0,0,0.2);
  border-radius: 10px;
}

.dark .custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.1);
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.2);
}
</style> 